import { EManagementType } from "api/enums";
import { IMeasurementData } from "api/interfaces";
import ContractRowModel from "api/models/ContractRowModel";
import CalculateMeasurementService from "api/services/CalculateMeasurementService";
import MeasurementRowListService from "api/services/MeasurementRowListService";
import MeasurementRowService from "api/services/MeasurementRowService";
import MeasurementService from "api/services/MeasurementService";
import { makeAutoObservable } from "mobx";
import { decimal, sum, valueToPercentual } from "utils/DecimalPrecision";
import {
  ContractRowStore,
  ContractStore,
  MeasurementListStore,
  MeasurementStore,
  MessageStore,
} from "../";
import MeasurementRowModel from "../../api/models/MeasurementRowModel";
import WBSModel from "../../api/models/WBSModel";
import BusyUpdateMeasurementService from "../../api/services/BusyUpdateMeasurementService";
import { lang } from "../../i18nLang";
import { formatNumber, formatNumberToMonetary } from "../../utils";

/**
 * Store de Medições
 * @category Measurements
 * @hideconstructor
 */

class MeasurementRowsStore {
  /** Lista contendo as linhas da medição atual. */
  private _rowsList: WBSModel<MeasurementRowModel> =
    new WBSModel<MeasurementRowModel>("");
  /** Lista com os IDs e nomes de todas as medições que serão exibidas como etapas. Essa lista é utilizada na
   * coluna de Medição Acumulada para exibir os valores de outras medições com o proposito de comparação. */
  private _stages: { uid: string; name: string }[] = [];
  /** Propriedade que define a etapa atual de Medição Acumulada. */
  public currentStage: number = 0;
  /** Flag que define se todas as linhas da medição atual foram carregadas em memória. */
  public allRowsLoaded: boolean = false;
  /** Propriedade que armazena a instância do serviço que faz a atualização das linhas após um delay. */
  private _updateService: BusyUpdateMeasurementService;
  /** Flag que indica se o sistema está atualizando ou calculando alguma linha da medição. */
  private _updateInProgress: boolean = false;
  /** Propriedade com o status atual de modificações das linhas.
   * Seu propóstio é fornecer um feedback dos processos executados em "segundo plano". */
  private _updateStatus: string = "";
  /** Guarda o rótulo e o valor total da etapa escolhida na coluna esquerda da tabela. Estas
   * informações serão exibidas no totalizador do canto superior direito.
   */
  public measuredTotalizer?: { label: string; value: number };

  constructor() {
    this._updateService = new BusyUpdateMeasurementService((newStatus) => {
      this._updateStatus = newStatus;
      this._updateInProgress = newStatus === "saving";
    });

    makeAutoObservable(this);
  }

  /** Retorna se o processo de atualização está em andamento. */
  isUpdateInProgress() {
    return this._updateInProgress;
  }

  /** Retorna se o processo de atualizações falhou. */
  updateHasFailed() {
    return this._updateStatus === "error";
  }

  /** Retorna a lista de linhas da medição atual. */
  public getAllRows() {
    return this._rowsList;
  }

  /** Retorna os nomes das medições que seram exibidas como etapas */
  public getStagesNames(): string[] {
    return this._stages.map((s) => s.name);
  }

  /** Retorna a mensagem tratada do status das modificações das linhas. */
  public getUpdateRowMessage(): string {
    if (!this._updateStatus) return "";
    const messages = lang(`measurements.updateStatus.${this._updateStatus}`);
    return messages || "";
  }

  /** Reseta a Store. */
  public reset() {
    this._rowsList = new WBSModel<MeasurementRowModel>("");
    this._stages = [
      { uid: "", name: lang("measurements.table.measuredTotal") },
      { uid: "", name: lang("measurements.table.measuredCurrent") },
    ];
    this.currentStage = 0;
    this.allRowsLoaded = false;
    this._updateStatus = "";
    this.measuredTotalizer = {
      label: lang("measurements.table.measuredTotal"),
      value: 0,
    };
  }

  /** Cria uma nova lista de linhas da medição e a popula com os dados retornados pelo Snapshot.
   * Ao terminar de buscar todas as linhas da medição do banco, a funçaõ altera a flag
   * para sinalizar que todas as linhas já foram obtidas. */
  subscribeMeasurementRows() {
    this.reset();
    const contract = ContractStore.getCurrentContract();
    const measurement = MeasurementStore.currentMeasurement;
    if (!contract || !measurement) return;

    this.setMeasurementStages(measurement.uid);

    this._rowsList = new WBSModel<MeasurementRowModel>(measurement.uid, 1000);
    MeasurementRowService.subscribeMeasurementRows(
      contract.uid,
      measurement.uid,
      this._rowsList,
      () => {
        if (!this.allRowsLoaded) this.validateMeasurementTotal();
        this.setAllRowsLoaded(true);
        this.setMeasuredColumnTotal(this.currentStage);
      }
    );
  }

  /** Cancela o snapshot para pausar o recebimento de atualizações. */
  unsubscribeMeasurementRows() {
    MeasurementRowService.unsubscribeRows();
    this.reset();
  }

  /** Obtém todas as medições menos a atual e cria uma lista somente com o ID e nome delas.
   * Então salva a nova lista na propriedade _stages. */
  private setMeasurementStages(measurementUid: string) {
    const measurements = MeasurementListStore.getMeasurements()
      .filter((m) => m.uid !== measurementUid)
      .reverse();

    this._stages = [
      ...this._stages,
      ...measurements.map((m) => ({ uid: m.uid, name: m.description })),
    ];
  }

  /** Valida se o total da medição bate com as linhas que estão no nível
   * principal do contrato e adiciona um log caso haja divergência.
   */
  private async validateMeasurementTotal() {
    const measurement = MeasurementStore.currentMeasurement;
    if (!measurement) return;

    const mainRows = this._rowsList.filter((row) => row.level === 1);
    const rowsTotal = mainRows.reduce((acc, row) => sum(acc, row.total), 0);

    if (!decimal(rowsTotal, 3).isEqualTo(measurement.total)) {
      await MeasurementService.addLogOfInvalidMeasurementTotal(
        measurement,
        rowsTotal
      );
    }
  }

  /** Obtém todas as linhas da medição e executa a rotina de exclusão. */
  async deleteMeasurementRows(): Promise<void> {
    const measurement = MeasurementStore.currentMeasurement;
    if (!measurement) return;

    if (!this._rowsList) throw new Error("Rows list not found!");

    const rows = this._rowsList.getAllRows();
    rows.forEach((row) => {
      this.getRowByCode(row.structured);
    });
    return MeasurementRowService.deleteMeasurementRows(measurement, rows);
  }

  /** Obtém e formata os dados de uma linha da medição para ser exibida na tabela. */
  getRowFormated(index: number) {
    const rowData = ContractRowStore.getRowFormated(index);
    if (!rowData) return {};

    const stage = rowData.isStage;
    const row = this.getRowByIndex(index);
    if (!row) return {};

    const billing = formatData(row.currentToBilling, "billing");
    const current = formatData(row, "current");
    const accumulated = formatData(this.getMeasuredData(row), "accumulated");
    const isBillingNegative = row.currentToBilling.total < 0;

    return {
      ...rowData,
      ...accumulated,
      ...billing,
      ...current,
      isBillingNegative,
    };

    function formatData({ quantity, percentual, total }, parcialKey: string) {
      return {
        [parcialKey + "Quantity"]: stage ? "" : formatNumber(quantity, 2, 4),
        [parcialKey + "Percentual"]: formatNumber(percentual),
        [parcialKey + "Total"]: formatNumberToMonetary(total, false),
      };
    }
  }

  /** Obtém os dados de outra medição caso esta tenha sido selecionada
   * nas etapas da coluna "Acumulado Total". */
  public getMeasuredData(row: MeasurementRowModel): IMeasurementData {
    if (this.currentStage <= 0) return row.measured; // Eh a medição acumulada total

    if (this.currentStage === 1) {
      return this.getMeasuredUntilCurrentData(
        row.uid,
        row.quantity,
        row.total,
        row.contractRow?.total
      );
    }

    return this.getOtherMeasurementRowData(row.structured);
  }

  /** Soma o valor acumulado da linha até a medição atual
   * com o valor da medição atual.
   */
  private getMeasuredUntilCurrentData(
    uid: string,
    quantity: number = 0,
    total: number = 0,
    totalAtContract: number = 0
  ): IMeasurementData {
    let measuredQuantity = 0;
    let measuredTotal = 0;

    const accumulated =
      MeasurementStore.currentAccumulatedUntilMeasurement.wbs.find(
        (row) => row.uid === uid
      );
    if (accumulated) {
      measuredQuantity = accumulated.quantity || 0;
      measuredTotal = accumulated.total || 0;
    }

    measuredTotal = sum(measuredTotal, total);
    return {
      quantity: sum(measuredQuantity, quantity),
      total: measuredTotal,
      percentual: valueToPercentual(measuredTotal, totalAtContract),
    };
  }

  /** Obtém o valor da linha na medição selecionada
   * na coluna da esquerda usando o código estruturado.
   */
  private getOtherMeasurementRowData(rowStructured: string): IMeasurementData {
    const listUid = this._stages[this.currentStage]?.uid;
    const list = MeasurementListStore.getRowsListByUid(listUid);
    if (list) {
      const measurement = this.getRowByCodeFromList(rowStructured, list);
      if (measurement) return measurement.currentMeasurement;
    }
    return {
      quantity: 0,
      total: 0,
      percentual: 0,
    };
  }

  /** Obtém a linha da medição e do contrato na lista informada por código. */
  public getRowByCodeFromList(
    code: string,
    list: WBSModel<MeasurementRowModel>
  ): MeasurementRowModel | undefined {
    const rowData = ContractRowStore.getRowByCode(code);
    if (!rowData) return;

    if (!list || !rowData) throw new Error("List and contract is required!");

    const row = MeasurementRowListService.getRowFromList(list, rowData);
    return row;
  }

  /** Executa todo o processo de recálculo e atualização da linha de medição alterada. */
  async changeMeasurements(
    index: number,
    dataKey: string,
    value: number
  ): Promise<boolean> {
    const contract = ContractStore.getCurrentContract();
    if (!contract) return false;

    const measurement = MeasurementStore.currentMeasurement;
    if (!measurement || !measurement.canEdit()) return false;

    const measurementRow = this.getRowByIndex(index);
    if (!measurementRow) return false;

    this._updateInProgress = true;
    this._updateStatus = "calculating";
    const field = dataKey.slice(7).toLowerCase();
    const rows = this.getRowsStructure(measurementRow.structured);
    const validateBalance =
      contract.managementType !== EManagementType.UNIT_PRICE;

    return CalculateMeasurementService.calculateRows(
      measurement,
      measurementRow,
      field,
      value,
      rows,
      MeasurementStore.currentMeasurementTotal,
      MeasurementStore.currentMeasuredTotal,
      validateBalance
    )
      .then(({ newTotal, newAccumulatedTotal }) => {
        MeasurementStore.setCurrentMeasurementTotal(newTotal);
        MeasurementStore.setCurrentMeasuredTotal(newAccumulatedTotal);
        this.clearUpdateStatus();

        /** Para melhora de performance o sistema filtra as linhas que foram editadas para salvar. */
        const rowsToUpdate = this._rowsList.filter((row) => row.isChanged);

        // Atualizar no banco as alterações feitas
        this._updateService.callUpdate(measurement, rowsToUpdate, newTotal);
        return true;
      })
      .catch((error) => {
        this._updateInProgress = this._updateService.isSavingChanges();
        this.clearUpdateStatus();
        MessageStore.error(error.message);
        return false;
      });
  }

  /** Limpa o status do projeto ou, caso esteja em atualização, o define
   * como 'saving'.
   */
  private clearUpdateStatus() {
    this._updateStatus = this._updateService.isSavingChanges() ? "saving" : "";
  }

  /**
   * Resta a service de atualização da medição para
   * receber as alterações da medição aberta.
   * @param {string} measurementUid ID da medição
   */
  initUpdateService(measurementUid: string) {
    const contract = ContractStore.getCurrentContract();
    if (!contract) {
      throw new Error("Contract not found!");
    }

    this._updateService.initToMeasurement(contract.uid, measurementUid);
  }

  /** Função que aciona novamente o processo de atualizar linhas editadas da medição. */
  retryLastUpdate() {
    this._updateService.retryUpdate();
  }

  /** Define o que será exibido no totalizador de acordo com
   * o item selecionado na coluna da esquerda, que pode ser
   * o total de 'Acumulado total', 'Acumulado até a medição
   * atual' ou o total de uma medição.
   */
  setMeasuredColumnTotal(stage: number) {
    if (stage === 0) {
      this.measuredTotalizer = {
        label: lang("measurements.table.measuredTotal"),
        value: MeasurementStore.currentMeasuredTotal,
      };
      return;
    }

    if (stage === 1) {
      this.measuredTotalizer = {
        label: lang("measurements.table.measuredCurrent"),
        value: sum(
          MeasurementStore.currentAccumulatedUntilMeasurement.total,
          MeasurementStore.currentMeasurementTotal
        ),
      };
      return;
    }

    const listUid = this._stages[stage]?.uid;
    const selectedMeasurement = MeasurementListStore.getMeasurements().find(
      (m) => m.uid === listUid
    );
    if (selectedMeasurement) {
      this.measuredTotalizer = {
        label: selectedMeasurement.description,
        value: selectedMeasurement.total,
      };
    }
  }

  /** Action que atualiza a etapa atual (currentStage) */
  setStage(newStage: number) {
    if (newStage < 0 || newStage >= this._stages.length) return;
    this.currentStage = newStage;
  }

  /** Obtém as linhas da medição seleciona na etapa. */
  async getRowsListByStage(stage: number) {
    if (stage === 1) {
      await MeasurementStore.getAccumulatedUntilCurrentMeasurement();
      return;
    }

    const listUid = this._stages[stage]?.uid;
    const list = MeasurementListStore.getRowsListByUid(listUid);
    if (!list && listUid) {
      await MeasurementListStore.getAndAddRowsToList(listUid);
    }
  }

  /** Obtém toda estrutura de linhas filhas e pais da linha com o código passado por parâmetro. */
  private getRowsStructure(structured: string) {
    const rows = ContractRowStore.getAllStructureByCode(structured);
    return rows.map((rowData) =>
      this.getOrCreateRowOnList(this._rowsList, rowData)
    );
  }

  /** Obtém a linha da medição atual e do contrato referente ao index passado por parâmetro.
   * Caso a linha ainda não exista na medição é criada uma nova.
   * Lembrando que o index se refere apenas as linhas que estejam marcadas como visíveis
   * na tabela.
   */
  private getRowByIndex(index: number): MeasurementRowModel {
    const rowData = ContractRowStore.getRowByIndex(index);
    if (!rowData) throw new Error("Row not found!");
    return this.getOrCreateRowOnList(this._rowsList, rowData);
  }

  /** Obtém a linha da medição atual e do contrato referente ao código passado por parâmetro.
   * Caso a linha ainda não exista na medição é criada uma nova.
   */
  private getRowByCode(code: string): MeasurementRowModel {
    const rowData = ContractRowStore.getRowByCode(code);
    if (!rowData) throw new Error("Row not found!");
    return this.getOrCreateRowOnList(this._rowsList, rowData);
  }

  private getOrCreateRowOnList(
    list: WBSModel<MeasurementRowModel>,
    contractRow: ContractRowModel
  ): MeasurementRowModel {
    if (!list || !contractRow)
      throw new Error("List and contract is required!");

    const row = MeasurementRowListService.getRowFromList(list, contractRow);
    if (row) return row;
    return MeasurementRowListService.addRowToList(list, contractRow);
  }

  /** Action que altera o valor da propriedade allRowsLoaded. */
  private setAllRowsLoaded(value: boolean) {
    if (value !== this.allRowsLoaded) this.allRowsLoaded = value;
  }
}

export default new MeasurementRowsStore();
